import { db } from "./db";
import type { IsoTimestamp } from "../../domain/time";

/**
 * Surviving a restart mid-order (FR-044).
 *
 * The scenario: staff are halfway through a six-drink order, the tablet's
 * battery dies or Chrome is killed by the OS, and it comes back. Without this,
 * the order is gone and the customer is asked to say it again — during an
 * outage, when nothing can be recovered from a server either.
 *
 * WHAT THIS MUST NOT DO IS SLOW THE TILL DOWN. `useCart` is deliberately
 * synchronous and in-memory: there is no await between tapping an item and the
 * total changing, which is how the sub-100 ms budget is met by construction
 * rather than by optimisation. So saving is FIRE-AND-FORGET and debounced —
 * the cart state is authoritative in memory, and this is a shadow copy that
 * trails it by a fraction of a second.
 *
 * The consequence, accepted deliberately: a restart within the debounce window
 * can lose the last tap. Losing one tap is a far better trade than putting an
 * IndexedDB write on the critical path of every tap.
 *
 * An in-progress cart is NOT a sale. It holds no document number, and
 * abandoning it consumes nothing (FR-015).
 */

/** There is one active cart per terminal. Held orders (US-later) get their own ids. */
export const ACTIVE_CART_ID = "active";

const SAVE_DEBOUNCE_MS = 250;

export interface PersistedCart {
  lines: unknown[];
  shiftId: string | null;
  updatedAt: IsoTimestamp;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let queued: { lines: unknown[]; shiftId: string | null } | null = null;

/**
 * Queue a save. Returns immediately.
 *
 * Repeated calls collapse: only the most recent state is written, because
 * intermediate states of a cart being built have no value to recover.
 */
export function saveCartSoon(lines: unknown[], shiftId: string | null): void {
  queued = { lines, shiftId };

  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    const pending = queued;
    queued = null;
    if (pending) void writeCart(pending.lines, pending.shiftId);
  }, SAVE_DEBOUNCE_MS);
}

/** Write now, without waiting for the debounce. Used before a deliberate unload. */
export async function flushCart(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const pending = queued;
  queued = null;
  if (pending) await writeCart(pending.lines, pending.shiftId);
}

async function writeCart(lines: unknown[], shiftId: string | null): Promise<void> {
  try {
    if (lines.length === 0) {
      await db.carts.delete(ACTIVE_CART_ID);
      return;
    }

    await db.carts.put({
      id: ACTIVE_CART_ID,
      label: null,
      state: { lines, shiftId, updatedAt: new Date().toISOString() } satisfies PersistedCart,
      updatedAt: new Date().toISOString(),
    });
  } catch {
    // A failed cart save must never surface to staff or interrupt a sale. The
    // cart is still correct in memory; only the recovery copy is missing, and
    // the storage warning (FR-048) is what tells someone about the real cause.
  }
}

/**
 * Restore the cart left behind by the previous session.
 *
 * Scoped to a shift. A cart built under yesterday's shift must not reappear
 * over today's opening float — it would be priced against a closed shift and
 * would land in the wrong Z reading.
 */
export async function restoreCart(shiftId: string | null): Promise<unknown[] | null> {
  try {
    const row = await db.carts.get(ACTIVE_CART_ID);
    if (!row) return null;

    const state = row.state as PersistedCart | undefined;
    if (!state?.lines?.length) return null;
    if (state.shiftId !== shiftId) {
      await db.carts.delete(ACTIVE_CART_ID);
      return null;
    }

    return state.lines;
  } catch {
    return null;
  }
}

export async function clearPersistedCart(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  queued = null;
  await db.carts.delete(ACTIVE_CART_ID);
}
